import React, { createContext, useState, useCallback } from "react";
import { createTheme } from "@mui/material";
import { themeType } from "../constants/theme-type";

export const ThemeContext = createContext();

const createThemes = (typeOfTheme) => {
  return createTheme({
    palette: {
      mode: 'dark', // Force dark mode for better contrast with the background
      primary: {
        main: "#0860d3",
        dark: "#0555aa",
        light: "#7dd3fc",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#8B5CF6",
        dark: "#6D28D9",
        light: "#A78BFA",
      },
      background: {
        default: "transparent",
        paper: "rgba(255, 255, 255, 0.05)",
      },
      text: {
        primary: "#F8FAFC",
        secondary: "#94A3B8",
      },
      type: typeOfTheme,
    },
    typography: {
      fontFamily: "'Outfit', 'Inter', 'Roboto', 'Helvetica', sans-serif",
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            background: "rgba(255, 255, 255, 0.04)",
            backdropFilter: "blur(24px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: "16px",
            color: "#F8FAFC",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: "linear-gradient(135deg, rgba(13,27,62,0.95) 0%, rgba(2,6,23,0.98) 100%)",
            backdropFilter: "blur(40px)",
            border: "1px solid rgba(56,189,248,0.2)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
            borderRadius: "24px",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: "linear-gradient(135deg, rgba(13,27,62,0.95) 0%, rgba(2,6,23,0.98) 100%)",
            backdropFilter: "blur(40px)",
            border: "1px solid rgba(56,189,248,0.2)",
            boxShadow: "0 -24px 60px rgba(0,0,0,0.6)",
            color: "#F8FAFC",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: "#F8FAFC",
            fontWeight: 700,
            fontSize: "1.25rem",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "12px",
            padding: "8px 16px",
          },
          contained: {
            background: "linear-gradient(90deg, #0555aa 0%, #0860d3 100%)",
            color: "#ffffff",
            boxShadow: "0 4px 14px rgba(5,85,170,0.4)",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(5,85,170,0.6)",
              transform: "translateY(-1px)",
            },
          },
          outlined: {
            borderColor: "rgba(56,189,248,0.5)",
            color: "#0860d3",
            "&:hover": {
              borderColor: "#0860d3",
              background: "rgba(56,189,248,0.1)",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "12px",
            background: "rgba(255, 255, 255, 0.03)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255, 255, 255, 0.15)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(56,189,248,0.4)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#0860d3",
            },
          },
          input: {
            color: "#F8FAFC",
            padding: "12px 14px",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: "#94A3B8",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: "#94A3B8",
            "&.Mui-focused": {
              color: "#0860d3",
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            color: "#94A3B8",
            "&.Mui-selected": {
              color: "#0860d3",
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: "#0860d3",
            height: "3px",
            borderTopLeftRadius: "3px",
            borderTopRightRadius: "3px",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            background: "rgba(13, 27, 62, 0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: "#E2E8F0",
            "&:hover": {
              background: "rgba(56,189,248,0.15)",
            },
          },
        },
      },
    },
  });
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(
    createThemes(themeType.light)
  );

  const contextValue = {
    currentTheme,
    changeTheme: useCallback((type) => onChangeThemeType(type), []),
  };

  const onChangeThemeType = (typeOfTheme) => {
    const theme = createThemes(typeOfTheme);
    setCurrentTheme(theme);
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
